/**
 * API Client — Drop-in replacement for fetch() with automatic JWT auth.
 *
 * Every component that was previously using:
 *     fetch('/api/...', { credentials: 'include' })
 *
 * Should now use:
 *     import { apiFetch } from '../services/apiClient';
 *     apiFetch('/api/...')
 *
 * This wrapper:
 *  1. Attaches the Authorization: Bearer <token> header automatically.
 *  2. Retries once with a refreshed token on 401 responses.
 *  3. Redirects to /login if refresh also fails.
 *  4. Preserves the full fetch() API (same signature).
 */

import { getAccessToken, refreshAccessToken, clearAuth } from './authService';
import { getBackendUrl } from '../utils/backendUrl';
import { getUserFacingErrorMessage } from '../utils/apiErrorMessage';

/**
 * Authenticated fetch wrapper.
 *
 * @param {string} url        — Relative path (e.g. '/api/jobs') or absolute URL.
 * @param {RequestInit} options — Standard fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export const apiFetch = async (url, options = {}) => {
  const backendUrl = getBackendUrl();

  // Resolve relative URLs against the backend
  const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;

  // Build headers — merge caller's headers with auth header
  const token = getAccessToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Don't set Content-Type for FormData (browser sets it with boundary)
  // Only set for JSON if not already set and body is not FormData
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const fetchOptions = {
    ...options,
    headers,
  };

  // Remove credentials: 'include' — we use Bearer tokens now
  delete fetchOptions.credentials;

  let response = await fetch(fullUrl, fetchOptions);

  // If we get a 401 and we have a token, try refreshing
  if (response.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry with the new token
      headers.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(fullUrl, { ...fetchOptions, headers });
    } else {
      // Refresh failed — redirect to login
      clearAuth();
      // Only redirect if not already on login page
      if (!window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/auth/callback') &&
          !window.location.pathname.startsWith('/apply/')) {
        window.location.href = '/login';
      }
    }
  }

  return response;
};

/**
 * Convenience: apiFetch that auto-parses JSON response.
 *
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<any>} Parsed JSON
 */
export const apiFetchJson = async (url, options = {}) => {
  const response = await apiFetch(url, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = getUserFacingErrorMessage(
      response.status,
      errorData,
      `Request failed (${response.status})`
    );
    const error = new Error(message);
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  return response.json();
};

/**
 * Re-export: use after `apiFetch` when the caller would otherwise
 * `if (!response.ok) throw new Error('Failed to fetch …')` so suspended / 403
 * messages match across the app.
 */
export {
  getUserFacingErrorMessage,
  getUserFacingErrorMessageFromResponse,
  throwIfNotOk,
} from '../utils/apiErrorMessage';

/**
 * Get the resolved backend URL (useful for non-fetch redirects like OAuth).
 */
export { getBackendUrl };

export default apiFetch;



